import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import GoogleMap from '@/components/ui/GoogleMap';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { LocationDetailsForm, ConnectionInfoForm } from '@/components/forms';
import type { Coordinates, MarkerFormData, Marker } from '@/types';
import api from '@/services/api';

const MarkerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [selectedPosition, setSelectedPosition] = useState<Coordinates | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  // Form state
  const [formData, setFormData] = useState<MarkerFormData>({
    coordinates: { latitude: 0, longitude: 0 },
    streetName: '',
    streetNameCorrection: '',
    currentLine: {
      operator: '',
      technology: 'Unknown',
      maxSpeed: {
        download: 0,
        upload: 0,
        unit: 'Mbps'
      },
      isActivated: false
    }
  });

  useEffect(() => {
    if (isEdit && id) {
      fetchMarker();
    }
  }, [isEdit, id]);

  const fetchMarker = async () => {
    if (!id) return;
    
    try {
      setInitialLoading(true);
      const response = await api.get(`/markers/${id}`);
      
      if (response.data.success) {
        const marker: Marker = response.data.data;
        setFormData({
          coordinates: marker.coordinates,
          streetName: marker.streetName,
          streetNameCorrection: marker.streetNameCorrection || '',
          currentLine: marker.currentLine
        });
        setSelectedPosition(marker.coordinates);
      }
    } catch (error) {
      console.error('Error fetching marker:', error);
      toast.error('Errore nel caricamento della segnalazione');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleMapClick = useCallback((coordinates: Coordinates) => {
    setSelectedPosition(coordinates);
    setFormData(prev => ({
      ...prev,
      coordinates
    }));
  }, []);

  const handleAddressSelect = useCallback((address: string, coordinates: Coordinates) => {
    setSelectedPosition(coordinates);
    setFormData(prev => ({
      ...prev,
      coordinates,
      streetName: address
    }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPosition) {
      toast.error('Seleziona una posizione sulla mappa');
      return;
    }

    if (!formData.streetName.trim()) {
      toast.error('Inserisci il nome della via');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        ...formData,
        coordinates: selectedPosition
      };

      let response;
      if (isEdit && id) {
        response = await api.put(`/markers/${id}`, payload);
      } else {
        response = await api.post('/markers', payload);
      }

      if (response.data.success) {
        toast.success(`Segnalazione ${isEdit ? 'aggiornata' : 'creata'} con successo!`);
        navigate('/admin/markers');
      }
    } catch (error: unknown) {
      console.error('Form submission error:', error);
      const message = (error as any).response?.data?.message || `Errore nel ${isEdit ? 'aggiornamento' : 'creazione'} della segnalazione`;
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="mb-6">
          <Link
            to="/admin/markers"
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            ← Torna alle segnalazioni
          </Link>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isEdit ? 'Modifica Segnalazione' : 'Nuova Segnalazione'}
          </h1>
          <p className="text-gray-600">
            {isEdit ? 'Aggiorna le informazioni della segnalazione' : 'Aggiungi una nuova segnalazione manualmente'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Map Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">1. Seleziona la Posizione</h2>
            <p className="text-gray-600 mb-4">
              Usa la barra di ricerca sulla mappa per trovare un indirizzo, oppure clicca direttamente sulla mappa per indicare la posizione.
            </p>
            
            <GoogleMap
              center={selectedPosition || { latitude: 41.9028, longitude: 12.4964 }}
              zoom={selectedPosition ? 15 : 10}
              height="400px"
              showSearchBox={true}
              onMapClick={handleMapClick}
              onAddressSelect={handleAddressSelect}
              markers={selectedPosition ? [{
                position: selectedPosition,
                title: 'Posizione selezionata',
                info: 'Segnalazione qui'
              }] : []}
            />

            {selectedPosition && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-800 text-sm">
                  ✓ Posizione selezionata: {selectedPosition.latitude.toFixed(6)}, {selectedPosition.longitude.toFixed(6)}
                </p>
              </div>
            )}
          </div>

          {/* Location Details */}
          <LocationDetailsForm
            formData={{
              streetName: formData.streetName || '',
              streetNameCorrection: formData.streetNameCorrection || ''
            }}
            onChange={(data) => setFormData(prev => ({ ...prev, ...data }))}
            showTitle={true}
            title="2. Dettagli della Posizione"
          />

          {/* Current Connection */}
          <ConnectionInfoForm
            currentLine={formData.currentLine || {}}
            onChange={(data) => setFormData(prev => ({
              ...prev,
              currentLine: { ...prev.currentLine, ...data }
            }))}
            showTitle={true}
            title="3. Informazioni Connessione"
          />

          {/* Submit Button */}
          <div className="flex justify-center space-x-4">
            <Link
              to="/admin/markers"
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Annulla
            </Link>
            
            <button
              type="submit"
              disabled={isLoading || !selectedPosition}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : null}
              {isLoading ? 
                (isEdit ? 'Aggiornando...' : 'Salvando...') : 
                (isEdit ? 'Aggiorna Segnalazione' : 'Crea Segnalazione')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarkerForm;